import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'pink-lava-ui';
import { Flex, type UploadFile } from 'antd';
import UploadDragger from '@/components/common/UploadDragger';
import type { ApiImportData, ApiResponse } from '@dc-trans/data-layer';
import type { TemplateConfig } from '@/utils/download';
import { useFileValidation } from '@/hooks/useFileValidation';
import { generateErrorFeedback } from '@/utils/errorFeedback';
import FileListItem from './FileListItem';
import { useNotification } from '@/context/notificationContext';
import { useUploadData } from '@/mutations';
import queryClient from '@/libs/queryClient';
import { isAxiosError } from 'axios';

interface UploadModalProps {
  // Core Modal Props
  visible: boolean;
  title?: string;

  // Upload Props
  accept?: string;
  multiple?: boolean;
  maxCount?: number;
  maxSize?: number; // in MB
  onCancel: () => void;

  // UI Customization
  width?: number | string;
  loading?: boolean;
  uploadButtonText?: string;
  cancelButtonText?: string;

  // Upload Dragger Customization
  uploadTitle?: string;
  uploadDescription?: string;
  uploadIcon?: React.ReactNode;
  excelTemplate?: Omit<TemplateConfig, 'data'>;
  uploadUrl: string;
  queryKey?: string;
  resourceName?: string;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  visible,
  title = 'Upload',
  accept,
  multiple = false,
  maxCount = 1,
  maxSize = 5,
  onCancel,
  width = 520,
  loading = false,
  uploadButtonText = 'Upload',
  uploadTitle,
  uploadDescription,
  uploadIcon,
  excelTemplate,
  uploadUrl,
  queryKey,
  resourceName = 'data',
}) => {
  const [triggerModalClose, setTriggerModalClose] = useState(false);
  const showNotification = useNotification();
  const { mutate: uploadData, isPending } = useUploadData({
    meta: {
      message: `Gagal mengunggah data ${resourceName}.`,
    },
  });

  // Custom hooks for file validation and upload state
  const {
    fileList,
    uploadError,
    progressCallback,
    validateAndSetFiles,
    removeFile,
    clearFiles,
    clearError,
    hasFiles,
    uploadResult,
    setUploadResult,
    setUploadError,
  } = useFileValidation({ maxSize, maxCount, multiple });

  // Ref to track current fileList to avoid stale closure issues
  const fileListRef = useRef(fileList);
  fileListRef.current = fileList;

  // Handle file change from UploadDragger
  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    clearError();
    setUploadResult(null);
    validateAndSetFiles(info.fileList);
  };

  const onSuccess = useCallback(
    (response: ApiResponse<ApiImportData>) => {
      const { data } = response;
      if (data.total_inserted > 0 && queryKey) queryClient.invalidateQueries({ queryKey: [queryKey] });

      if (data.total_error > 0) {
        showNotification.error({
          message: `Data ${resourceName} berhasil di upload, tetapi terdapat ${data.total_error} data yang tidak sesuai.`,
        });
        setUploadResult(data);
      } else {
        showNotification.success({
          message: `Data ${resourceName} berhasil diupload.`,
        });
        setTriggerModalClose(true);
      }
    },
    [showNotification, setUploadResult, queryKey, resourceName]
  );

  const onError = useCallback(
    (error: unknown) => {
      let message = 'Terjadi kesalahan saat mengunggah data.';
      if (isAxiosError<ApiResponse<ApiImportData>>(error)) {
        message = error.response?.data?.message ?? error.message ?? message;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      setUploadError(message);
    },
    [setUploadError]
  );

  // Handle upload submission
  const handleUploadSubmission = useCallback(async () => {
    // Access current fileList state from ref to avoid stale closure
    const currentFiles = fileListRef.current;

    if (!currentFiles.length || !currentFiles[0].originFileObj) {
      setUploadError('No valid file selected for upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', currentFiles[0].originFileObj as any);
    uploadData(
      {
        url: uploadUrl,
        data: formData,
        progressCallback,
      },
      {
        onSuccess,
        onError,
      }
    );
  }, [uploadData, progressCallback, uploadUrl, onSuccess, onError, setUploadError]);

  // Handle modal close
  const handleCancel = useCallback(
    (skipConfirm?: boolean) => {
      if (!skipConfirm && hasFiles) {
        const confirmed = window.confirm('You have selected files. Are you sure you want to close?');
        if (!confirmed) return;
      }

      // Reset state
      setTriggerModalClose(false);
      clearFiles();
      setUploadResult(null);
      onCancel();
    },
    [hasFiles, clearFiles, setUploadResult, onCancel]
  );

  // Handle file removal
  const handleRemove = useCallback(
    (file: UploadFile) => {
      removeFile(file);
      setUploadResult(null);
    },
    [removeFile, setUploadResult]
  );

  // Handle error feedback download
  const handleDownloadErrorFeedback = useCallback(() => {
    if (uploadResult && excelTemplate) {
      generateErrorFeedback({ uploadResult, excelTemplate });
    }
  }, [uploadResult, excelTemplate]);

  const isLoading = isPending || loading;

  const canUpload = hasFiles && !uploadError && !isLoading;

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={() => handleCancel()}
      width={width}
      footer={() => (
        <UploadButton
          onClick={handleUploadSubmission}
          loading={isLoading}
          disabled={!canUpload}
          uploadButtonText={uploadButtonText}
          onAutoClose={() => handleCancel(true)}
          triggerModalClose={triggerModalClose}
        />
      )}
      centered
      destroyOnHidden
    >
      <Flex vertical gap={16}>
        {/* File List Info */}
        {hasFiles && (
          <Flex vertical gap={8}>
            {fileList.map(item => (
              <FileListItem
                key={item.uid}
                file={item}
                isLoading={isLoading}
                uploadResult={uploadResult}
                uploadError={uploadError}
                onRemove={handleRemove}
                onDownloadErrorFeedback={handleDownloadErrorFeedback}
              />
            ))}
          </Flex>
        )}
        <UploadDragger
          accept={accept}
          multiple={multiple}
          fileList={fileList}
          onChange={handleFileChange}
          onRemove={handleRemove}
          beforeUpload={() => false} // Prevent auto upload
          title={uploadTitle}
          description={uploadDescription}
          icon={uploadIcon}
          isCompact={(uploadResult?.total_error || 0) > 0}
        />
      </Flex>
    </Modal>
  );
};

const UploadButton = React.memo(
  ({
    onClick,
    loading,
    disabled,
    uploadButtonText,
    onAutoClose,
    triggerModalClose,
  }: {
    onClick: () => void;
    loading: boolean;
    disabled: boolean;
    uploadButtonText: string;
    onAutoClose: () => void;
    triggerModalClose: boolean;
  }) => {
    const [countdownTimer, setCountdownTimer] = useState<number | null>(null);

    useEffect(() => {
      if (triggerModalClose) {
        setCountdownTimer(3);

        const interval = setInterval(() => {
          setCountdownTimer(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(interval);
              // Defer the onAutoClose call to prevent state update during rendering
              setTimeout(() => onAutoClose(), 0);
              return null;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(interval);
      }
    }, [triggerModalClose, onAutoClose]);

    const buttonText = countdownTimer !== null ? `Tutup (${countdownTimer})` : uploadButtonText;

    const handleClick = useCallback(() => {
      if (countdownTimer === null) {
        onClick();
      } else {
        onAutoClose();
      }
    }, [countdownTimer, onClick, onAutoClose]);

    return (
      <Button
        type="primary"
        shape="round"
        className="pt-4"
        block
        onClick={handleClick}
        loading={loading}
        disabled={disabled}
      >
        {buttonText}
      </Button>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.loading === nextProps.loading &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.uploadButtonText === nextProps.uploadButtonText &&
      prevProps.triggerModalClose === nextProps.triggerModalClose
    );
  }
);

UploadButton.displayName = 'UploadButton';

export default UploadModal;
